import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTradeGroupTimeline, TimelineCandle, TimelineEvent, TimelineEventType, TradePair } from '../api/tradeGroups';
import { formatDollar } from '../utils/currencyFormatter';
import {
  createChart,
  ColorType,
  CandlestickData,
  Time,
  IChartApi,
  ISeriesApi,
  CandlestickSeries,
  SeriesMarker,
  SeriesMarkerPosition,
  SeriesMarkerShape,
  createSeriesMarkers
} from 'lightweight-charts';
import './TimelineChart.css';

interface TimelineChartProps {
  tradeGroupId: string;
  productId: string;
  tradeGroupCreatedAt: number; // epoch millis
  granularityMins?: number; // Granularity in minutes
  startDate?: string; // Date in YYYY-MM-DD format
  endDate?: string; // Date in YYYY-MM-DD format
}

// Event marker configuration
interface EventMarkerConfig {
  color: string;
  shape: SeriesMarkerShape;
  position: SeriesMarkerPosition;
  label: string;
}

const EVENT_MARKER_CONFIG: Record<TimelineEventType, EventMarkerConfig> = {
  BUY_FILL: { color: '#22c55e', shape: 'circle', position: 'belowBar', label: 'B' },
  SELL_FILL: { color: '#3b82f6', shape: 'circle', position: 'aboveBar', label: 'S' },
  DIP_BUYING: { color: '#a855f7', shape: 'arrowUp', position: 'belowBar', label: 'D' },
  REBALANCE: { color: '#f97316', shape: 'square', position: 'aboveBar', label: 'R' },
  RANGE_CREATED: { color: '#10b981', shape: 'arrowUp', position: 'belowBar', label: '+' },
  RANGE_AGED_OUT: { color: '#6b7280', shape: 'arrowDown', position: 'aboveBar', label: '-' },
  CAPITAL_REDEPLOYED: { color: '#0ea5e9', shape: 'square', position: 'belowBar', label: 'C' },
  HOT_MONEY_REINVESTMENT: { color: '#ec4899', shape: 'arrowUp', position: 'belowBar', label: 'H' },
};

const TimelineChart: React.FC<TimelineChartProps> = ({
  tradeGroupId,
  productId,
  tradeGroupCreatedAt,
  granularityMins = 1,  // Default to 1-minute candles
  startDate,
  endDate,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const [showResetZoom, setShowResetZoom] = useState(false);
  const [hoveredEvents, setHoveredEvents] = useState<TimelineEvent[]>([]);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [modalEvents, setModalEvents] = useState<TimelineEvent[] | null>(null); // For click-to-open modal
  const [hoveredTradePair, setHoveredTradePair] = useState<TradePair | null>(null);
  const [tradePairTooltipPos, setTradePairTooltipPos] = useState<{ x: number; y: number } | null>(null);

  // Calculate date range from string dates or use defaults
  const { startDateMillis, endDateMillis } = useMemo(() => {
    const end = Date.now();
    let start = end - (2 * 24 * 60 * 60 * 1000); // Default 2 days

    if (startDate && endDate) {
      // Convert YYYY-MM-DD to epoch millis
      const startParts = startDate.split('-').map(Number);
      const endParts = endDate.split('-').map(Number);
      start = new Date(startParts[0], startParts[1] - 1, startParts[2]).getTime();
      const endFromDate = new Date(endParts[0], endParts[1] - 1, endParts[2], 23, 59, 59).getTime();
      return { startDateMillis: start, endDateMillis: endFromDate };
    }

    return { startDateMillis: start, endDateMillis: end };
  }, [startDate, endDate]);

  // Fetch timeline data
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trade-group-timeline', tradeGroupId, granularityMins, startDate, endDate],
    queryFn: () => getTradeGroupTimeline(tradeGroupId, startDateMillis, endDateMillis, granularityMins),
    retry: 2,
    staleTime: 30000, // 30 seconds
  });

  // Convert events to markers
  const eventMarkers = useMemo(() => {
    if (!data?.events || data.events.length === 0) return [];

    return data.events
      .filter(event => event.price !== null)
      .map((event): SeriesMarker<Time> => {
        const config = EVENT_MARKER_CONFIG[event.type] || EVENT_MARKER_CONFIG.BUY_FILL;
        return {
          time: (event.timestamp / 1000) as Time,
          price: event.price as number,
          position: config.position,
          color: config.color,
          shape: config.shape,
          text: config.label,
          size: 1,
        };
      });
  }, [data?.events]);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current || isLoading || !data || data.candles.length === 0) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: 'rgba(148, 163, 184, 0.1)' },
        horzLines: { color: 'rgba(148, 163, 184, 0.2)' },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
        horzLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
      },
      timeScale: {
        borderColor: '#475569',
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: {
        borderColor: '#475569',
      },
      width: chartContainerRef.current.clientWidth,
      height: data?.tradePairs && data.tradePairs.length > 0 ? 600 : 500, // Extra height for trade pair bars
    });

    chartRef.current = chart;

    // Create candlestick series
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });

    candleSeriesRef.current = candleSeries;

    // Convert candles to lightweight-charts format
    const chartData: CandlestickData<Time>[] = data.candles.map((candle: TimelineCandle) => ({
      time: (candle.timestamp / 1000) as Time, // lightweight-charts expects seconds
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }));

    candleSeries.setData(chartData);

    // Add event markers
    if (eventMarkers.length > 0) {
      createSeriesMarkers(candleSeries, eventMarkers);
    }

    // Add vertical line marker for trade group creation date
    if (tradeGroupCreatedAt) {
      const creationTimeSec = Math.floor(tradeGroupCreatedAt / 1000);
      
      // Find if creation time is within the candle range
      const firstCandleTime = data.candles[0]?.timestamp / 1000;
      const lastCandleTime = data.candles[data.candles.length - 1]?.timestamp / 1000;
      
      if (creationTimeSec >= firstCandleTime && creationTimeSec <= lastCandleTime) {
        // Add a price line at the creation time (approximate with first candle's open price)
        const creationPrice = data.candles.find(c => c.timestamp / 1000 >= creationTimeSec)?.open || data.candles[0].open;
        
        candleSeries.createPriceLine({
          price: creationPrice,
          color: '#8B5CF6',
          lineWidth: 2,
          lineStyle: 2, // dashed
          axisLabelVisible: false, // Hide right-side label
          title: 'TG Created', // Shorter title for left side
          lineVisible: true,
        });
      }
    }

    // Fit content
    chart.timeScale().fitContent();

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chart) {
        chart.applyOptions({ 
          width: chartContainerRef.current.clientWidth 
        });
      }
    };

    window.addEventListener('resize', handleResize);

    // Track zoom state
    const handleVisibleRangeChange = () => {
      setShowResetZoom(true);
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);

    // Handle crosshair move for event tooltip
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time || !data?.events) {
        setHoveredEvents([]);
        setTooltipPosition(null);
        return;
      }

      // Find ALL events near the crosshair time
      const crosshairTime = (param.time as number) * 1000; // Convert to millis
      const windowMs = data.granularity * 60 * 1000;

      const eventsInWindow = data.events.filter(event => {
        const timeDiff = Math.abs(event.timestamp - crosshairTime);
        return timeDiff < windowMs;
      });

      if (eventsInWindow.length > 0 && chartContainerRef.current) {
        const rect = chartContainerRef.current.getBoundingClientRect();
        setHoveredEvents(eventsInWindow);
        setTooltipPosition({
          x: param.point.x + rect.left + 10,
          y: param.point.y + rect.top - 10,
        });
      } else {
        setHoveredEvents([]);
        setTooltipPosition(null);
      }
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
    };
  }, [data, isLoading, tradeGroupCreatedAt, eventMarkers]);

  // Handle reset zoom
  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
      setShowResetZoom(false);
    }
  };

  // Format currency helper using currencyFormatter
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return formatDollar(value, productId);
  };

  // Format single event card
  const formatEventCard = (event: TimelineEvent, index: number): React.ReactNode => {
    const config = EVENT_MARKER_CONFIG[event.type];
    const date = new Date(event.timestamp);
    const formattedTime = date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    return (
      <div key={index} className="event-card" style={{ borderLeftColor: config?.color }}>
        <div className="event-card-header">
          <span
            className="event-type-badge"
            style={{ backgroundColor: config?.color }}
          >
            {config?.label}
          </span>
          <span className="event-type-name">{event.type.replace(/_/g, ' ')}</span>
          <span className="event-time">{formattedTime}</span>
        </div>
        <div className="event-card-body">
          {event.price && (
            <div className="event-detail-inline">
              <span className="detail-label">Price:</span>
              <span className="detail-value">{formatCurrency(event.price)}</span>
            </div>
          )}
          {event.amount && (
            <div className="event-detail-inline">
              <span className="detail-label">Amount:</span>
              <span className="detail-value">{formatCurrency(event.amount)}</span>
            </div>
          )}
          {event.details.filledSize && (
            <div className="event-detail-inline">
              <span className="detail-label">Filled:</span>
              <span className="detail-value">{event.details.filledSize.toFixed(6)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Format multiple events tooltip - shows aggregate summary
  const formatEventsTooltip = (events: TimelineEvent[]): React.ReactNode => {
    const date = new Date(events[0].timestamp);
    const formattedDate = date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    // Count events by type
    const typeCounts: Record<string, number> = {};
    events.forEach(event => {
      typeCounts[event.type] = (typeCounts[event.type] || 0) + 1;
    });

    // Build summary text
    const summaryParts = Object.entries(typeCounts).map(([type, count]) => {
      const config = EVENT_MARKER_CONFIG[type as TimelineEventType];
      const label = type.replace(/_/g, ' ').toLowerCase();
      return { type, count, label, color: config?.color || '#888' };
    });

    return (
      <div className="events-tooltip-compact">
        <div className="events-tooltip-date">{formattedDate}</div>
        <div className="events-tooltip-summary">
          {summaryParts.map(({ type, count, label, color }) => (
            <span key={type} className="event-summary-item">
              <span className="event-summary-dot" style={{ backgroundColor: color }}></span>
              <span className="event-summary-text">{count} {label}{count > 1 ? 's' : ''}</span>
            </span>
          ))}
        </div>
        <div className="events-tooltip-hint">Click for details</div>
      </div>
    );
  };

  // Handle chart click to open modal
  const handleChartClick = () => {
    if (hoveredEvents.length > 0) {
      setModalEvents(hoveredEvents);
    }
  };

  // Close modal
  const closeModal = () => {
    setModalEvents(null);
  };

  // Format modal content with full event details
  const formatEventsModal = (events: TimelineEvent[]): React.ReactNode => {
    const date = new Date(events[0].timestamp);
    const formattedDate = date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <div className="events-modal-overlay" onClick={closeModal}>
        <div className="events-modal" onClick={(e) => e.stopPropagation()}>
          <div className="events-modal-header">
            <div className="events-modal-title">
              <span className="events-modal-date">{formattedDate}</span>
              <span className="events-modal-count">{events.length} event{events.length > 1 ? 's' : ''}</span>
            </div>
            <button className="events-modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="events-modal-body">
            {events.map((event, index) => formatEventCard(event, index))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="timeline-chart-container">
        <div className="timeline-chart-loading">
          <div className="spinner"></div>
          <p>Loading timeline data...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="timeline-chart-container">
        <div className="timeline-chart-error">
          <div className="error-icon">⚠️</div>
          <div className="error-message">
            <h3>Failed to load timeline data</h3>
            <p>{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
          </div>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.candles.length === 0) {
    return (
      <div className="timeline-chart-container">
        <div className="timeline-chart-empty">
          <div className="empty-icon">📈</div>
          <div className="empty-title">No Timeline Data Available</div>
          <div className="empty-description">
            Timeline will appear once market data is available for {productId}.
          </div>
        </div>
      </div>
    );
  }

  // Count events by type for legend
  const eventCounts = data.events?.reduce((acc, event) => {
    acc[event.type] = (acc[event.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  // Render trade pair bars overlayed on chart
  const renderTradePairBars = () => {
    if (!data?.tradePairs || data.tradePairs.length === 0 || !chartRef.current) {
      return null;
    }

    const timeScale = chartRef.current.timeScale();
    const chartWidth = chartContainerRef.current?.clientWidth || 0;

    return data.tradePairs.map((pair, index) => {
      // Calculate bar position based on time scale
      const buyTime = (pair.buyTimestamp / 1000) as Time;
      const sellTime = pair.sellTimestamp ? (pair.sellTimestamp / 1000) as Time : null;
      
      try {
        const buyX = timeScale.timeToCoordinate(buyTime);
        if (buyX === null) return null;

        let width: number;
        if (sellTime) {
          const sellX = timeScale.timeToCoordinate(sellTime);
          if (sellX === null) return null;
          width = sellX - buyX;
        } else {
          // Unfilled sell - extend to right edge
          width = chartWidth - buyX - 10;
        }

        // Determine color based on profit
        let color: string;
        if (pair.profit === null) {
          color = '#6b7280'; // Gray for unfilled
        } else if (pair.profit > 0) {
          color = '#22c55e'; // Green for profit
        } else if (pair.profit < 0) {
          color = '#ef4444'; // Red for loss
        } else {
          color = '#6b7280'; // Gray for break-even
        }

        // Calculate bar Y position (stacked vertically from bottom of chart)
        const barHeight = 12;
        const barGap = 2;
        const chartHeight = data?.tradePairs && data.tradePairs.length > 0 ? 600 : 500;
        const barAreaTop = chartHeight - 100; // Reserve 100px at bottom for bars
        const yOffset = barAreaTop + (index % 6) * (barHeight + barGap); // Stack up to 6 bars

        return (
          <div
            key={pair.buyOrderId}
            className="trade-pair-bar"
            style={{
              position: 'absolute',
              left: `${buyX}px`,
              top: `${yOffset}px`,
              width: `${Math.max(width, 2)}px`,
              height: `${barHeight}px`,
              backgroundColor: color,
              borderRadius: '2px',
              cursor: 'pointer',
              opacity: 0.8,
              transition: 'opacity 0.2s',
              pointerEvents: 'auto',
            }}
            onMouseEnter={(e) => {
              setHoveredTradePair(pair);
              if (chartContainerRef.current) {
                const rect = chartContainerRef.current.getBoundingClientRect();
                setTradePairTooltipPos({
                  x: e.clientX,
                  y: rect.top + yOffset - 10,
                });
              }
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              setHoveredTradePair(null);
              setTradePairTooltipPos(null);
              e.currentTarget.style.opacity = '0.8';
            }}
          />
        );
      } catch (err) {
        return null;
      }
    });
  };

  return (
    <div className="timeline-chart-container">
      <div className="timeline-chart-header">
        <div className="timeline-chart-title">
          <h3>Price Timeline</h3>
          <span className="timeline-chart-subtitle">
            {productId} · {data.granularity}min candles · {data.candles.length} candles
            {data.events && data.events.length > 0 && ` · ${data.events.length} events`}
            {data.tradePairs && data.tradePairs.length > 0 && ` · ${data.tradePairs.length} trade pairs`}
          </span>
        </div>
        {showResetZoom && (
          <button onClick={handleResetZoom} className="reset-zoom-button">
            Reset Zoom
          </button>
        )}
      </div>

      {/* Enhanced Legend */}
      <div className="timeline-chart-legend">
        <div className="legend-item">
          <span className="legend-marker created"></span>
          <span className="legend-label">Trade Group Created</span>
        </div>
        {Object.entries(EVENT_MARKER_CONFIG).map(([type, config]) => {
          const count = eventCounts[type];
          if (!count) return null;
          return (
            <div key={type} className="legend-item">
              <span
                className="legend-marker-circle"
                style={{ backgroundColor: config.color }}
              >
                {config.label}
              </span>
              <span className="legend-label">
                {type.replace(/_/g, ' ')} ({count})
              </span>
            </div>
          );
        })}
        {data.tradePairs && data.tradePairs.length > 0 && (
          <>
            <div className="legend-item">
              <span className="legend-marker-bar" style={{ backgroundColor: '#22c55e' }}></span>
              <span className="legend-label">Profitable Trade</span>
            </div>
            <div className="legend-item">
              <span className="legend-marker-bar" style={{ backgroundColor: '#ef4444' }}></span>
              <span className="legend-label">Loss Trade</span>
            </div>
            <div className="legend-item">
              <span className="legend-marker-bar" style={{ backgroundColor: '#6b7280' }}></span>
              <span className="legend-label">Unfilled Trade</span>
            </div>
          </>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <div
          ref={chartContainerRef}
          className="timeline-chart"
          onClick={handleChartClick}
          style={{ cursor: hoveredEvents.length > 0 ? 'pointer' : 'default' }}
        />
        {/* Trade Pair Bars Overlay */}
        {data.tradePairs && data.tradePairs.length > 0 && (
          <div className="trade-pair-bars-container" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
            {renderTradePairBars()}
          </div>
        )}
      </div>

      {/* Trade Pair Tooltip */}
      {hoveredTradePair && tradePairTooltipPos && (
        <div
          className="trade-pair-tooltip"
          style={{
            left: tradePairTooltipPos.x,
            top: tradePairTooltipPos.y,
            position: 'fixed',
            backgroundColor: '#1e293b',
            border: '1px solid #475569',
            borderRadius: '6px',
            padding: '8px 12px',
            color: '#f1f5f9',
            fontSize: '12px',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            Trade Pair {hoveredTradePair.buyOrderId.substring(0, 8)}
          </div>
          <div>Buy: {formatCurrency(hoveredTradePair.buyPrice)}</div>
          {hoveredTradePair.sellPrice && (
            <div>Sell: {formatCurrency(hoveredTradePair.sellPrice)}</div>
          )}
          {hoveredTradePair.profit !== null && (
            <div style={{ color: hoveredTradePair.profit >= 0 ? '#22c55e' : '#ef4444' }}>
              P/L: {formatCurrency(hoveredTradePair.profit)} ({hoveredTradePair.profitPercent?.toFixed(2)}%)
            </div>
          )}
          {hoveredTradePair.sellTimestamp && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#94a3b8' }}>
              Duration: {Math.floor((hoveredTradePair.sellTimestamp - hoveredTradePair.buyTimestamp) / 60000)}m
            </div>
          )}
          {!hoveredTradePair.sellTimestamp && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#94a3b8' }}>
              Not yet sold
            </div>
          )}
        </div>
      )}

      {/* Event Tooltip - compact summary */}
      {hoveredEvents.length > 0 && tooltipPosition && (
        <div
          className="timeline-event-tooltip"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            position: 'fixed',
          }}
        >
          {formatEventsTooltip(hoveredEvents)}
        </div>
      )}

      {/* Events Detail Modal */}
      {modalEvents && formatEventsModal(modalEvents)}

      <div className="timeline-chart-footer">
        <div className="chart-help-text">
          💡 Click and drag to zoom, double-click to reset. Hover over markers for event details.
        </div>
      </div>
    </div>
  );
};

export default TimelineChart;
